import React, {useState} from "react";
import strapi from "/scripts/apis/strapi/index.js";
import css from "./index.module.scss";
import DefaultLayout from "layouts/default";
import ImageCard from "@/components/imageCard";
import ContactForm from "forms/contact";
import CustomerFaq from "sections/customerFaq";
import Picture from "@/components/picture";
import SellerFaq from "sections/sellerFaq";

export default function AboutPage({articles}) {
  return <DefaultLayout
    title="About | Lazy Prompts"
    light
    description="Lazy Prompts was founded to bridge the gap between human ingenuity and the immense power of artificial intelligence. We connect talented engineers with professional businesses."
  >
    <section className={`${css.fold}`}>
      <div className="sectionInner">
        <h1>Unleashing <span>AI-Fueled</span> Creativity</h1>
        <div className={css.text}><b>Lazy Prompts</b> was founded to bridge the gap between human ingenuity and the immense power of artificial intelligence. We connect <b>talented engineers</b> with <b>professional businesses</b>.</div>
      </div>
    </section>
    <section className={`light ${css.elaboration}`}>
      <div className="sectionInner">
        <div className={css.mission}>
          <h2>Our Mission</h2>
          <p>Our mission is to empower writers, marketers, and creative minds across the globe by providing them with an unparalleled marketplace for enterprise AI prompts, ideas, and inspiration. We believe that by blending human creativity with the power of AI, we can unlock a world of endless possibilities.</p>
          <p>Lazy Prompts was designed with one thing in mind — to provide maximum benefit to our customers. Time is a precious commodity, and our platform aims to help users work smarter, not harder. By offering a diverse array of AI-generated prompts tailored to enterprise needs, Lazy Prompts helps users quickly overcome writer&apos;s block, brainstorm more effectively, and develop unique content that stands out from the crowd.</p>
        </div>
        <div className={css.contact}>
          <h2>Contact Us</h2>
          <ContactForm/>
        </div>
      </div>
    </section>


    <section className={`light ${css.buyExplanation}`}>
      <div className="sectionInner">
        <h2>How Buying AI Prompts Drives Efficiency</h2>
        <div className={css.text}>
          <p>The potency of AI is no longer the stuff of science fiction but a tangible force in our everyday lives. One vital avenue through which this transformative power is harnessed is through AI prompts, bespoke instructions that elicit specific outputs from AI models.</p>
          <p>AI prompts underpin myriad operations that drive efficiency in today&apos;s enterprises. These prompts work behind the scenes, tailoring responses for customer inquiries, writing engaging content, optimizing advertisement campaigns, and even crafting persuasive sales pitches. By leveraging the computational might of AI, prompts eliminate the drudgery of routine tasks, expedite business processes, and ultimately, free up precious human capital for strategic endeavors.</p>
          <p>AI prompts are an extension of a business&apos;s toolkit, imbued with capabilities that surpass traditional methods in speed and scope. Their innate adaptability allows them to cater to diverse business needs, providing a competitive edge in an increasingly digitized and fast-paced world.</p>
        </div>

        <ImageCard
          picture={{
            base: "/images/misc/android/android",
            widths: [500, 1000, 1500, 2000, 2500, 3000, 3500, 4000],
            exts: ["webp", "png"],
          }}
          sizes="100vw"
          loading="lazy"
          overlaid
          light
          className={css.image}
        />
      </div>
    </section>

    <CustomerFaq/>


    <section className={`light ${css.articles}`}>
      <div className="sectionInner">
        <h2>Getting Started Selling AI Prompts</h2>
        <p>To set you off on the right foot, we have curated a selection of insightful articles on the art of prompt engineering. These guides demystify the process, elucidate best practices, and help you unlock your creativity in the realm of AI prompting. With these resources, you are poised to not just participate, but truly thrive as a prompt engineer in our community.</p>
        <div className={css.cards}>
          {articles.map(((article) => <ImageCard
            key={article.id}
            strapiPicture={article?.attributes?.coverArt?.data?.attributes}
            sizes="(max-width: 1300px) 50vw, (max-width: 650px) 100vw, 33vw"
            light
            href={`/articles/${article?.attributes?.slug}`}
            label={article?.attributes?.headline}
          >{article?.attributes?.subheadline}</ImageCard>))}
        </div>
      </div>
    </section>

    <SellerFaq/>
  </DefaultLayout>;
}

export async function getStaticProps() {
  let articles = await strapi.query(strapi.endpoints.ARTICLES_FIND, {
    fields: ["id", "headline", "slug", "subheadline", "publishedAt"],
    filters: {
      id: {$in: [15, 14, 12]},
    },
    populate: "*",
  });

  return {
    props: {
      articles,
    },
  };
}
